import { isNull } from 'lodash/fp';
import React, { useMemo } from 'react';

import styles from './NettingEngine.module.scss';
import { NettingEngineJurisdictionResultsSection } from './NettingEngineJurisdictionResultsSection';
import { JurisdictionalAnswers, JurisdictionalNettingDetailsAnswer, GMRAJurisdictionalNettingInformation, GMRAJurisdictionalNettingDetails, GMRAJurisdictionalCollateralNettingDetails } from './store';

interface GMRANettingEngineJurisdictionAnswersProps {
    jurisdictionalResults: GMRAJurisdictionalNettingInformation;
    viewCustomConclusions: boolean;
    createQuestionAnswer: (question: string, answer: JurisdictionalNettingDetailsAnswer, noPositive?: boolean, isLocalLaw?: boolean, aetApplicable?: string | undefined) => JurisdictionalAnswers;
}

export const GMRANettingEngineJurisdictionAnswers: React.FC<GMRANettingEngineJurisdictionAnswersProps> = ({ jurisdictionalResults, viewCustomConclusions, createQuestionAnswer }) => {

    const {
        details,
        collateralDetails,
        clientSignOffDetails,
        clientSignOffCollateralDetails
    } = jurisdictionalResults;

    const customConclusionsExist = useMemo(() => !isNull(clientSignOffDetails) && viewCustomConclusions, [clientSignOffDetails, viewCustomConclusions]);

    const { agreementCovered, counterpartyCovered, governingLawRecognised, closeOutNettingEnforceable, automaticEarlyTerminationRequired, onShoreInsolvency, offShoreInsolvency, multipleOfficesImpactAnswer } = customConclusionsExist ? (clientSignOffDetails as GMRAJurisdictionalNettingDetails)! : details as GMRAJurisdictionalNettingDetails;
    const { recharacterisationAsSecurityAdverselyImpactsEnforceability, rightToSetOffEnforceable, transferRespectedAsTitleTransfer } = customConclusionsExist ? (clientSignOffCollateralDetails as GMRAJurisdictionalCollateralNettingDetails)! : collateralDetails as GMRAJurisdictionalCollateralNettingDetails;

    const eachAnswer: JurisdictionalAnswers[] = useMemo(() => {
        const answers: JurisdictionalAnswers[] = [
            createQuestionAnswer('Is the selected Agreement covered by the opinion?', agreementCovered),
            createQuestionAnswer('Is the selected counterparty type covered by the opinion?', counterpartyCovered),
            createQuestionAnswer('Will the governing law of the contract be recognised?', governingLawRecognised),
            createQuestionAnswer('Is close out netting enforceable?', closeOutNettingEnforceable),
            createQuestionAnswer('Is automatic early termination required?', automaticEarlyTerminationRequired, true),
            createQuestionAnswer('Could separate insolvency proceedings be instituted against a local branch?', onShoreInsolvency, true),
            createQuestionAnswer('Would multi-branch close-out netting be respected in a separate local branch insolvency?', offShoreInsolvency)
        ];
        if (!isNull(multipleOfficesImpactAnswer)) {
            answers.push(createQuestionAnswer('Does having multiple designated offices impact the netting decision?', multipleOfficesImpactAnswer));
        }
        return answers;
    }, [agreementCovered, counterpartyCovered, governingLawRecognised, createQuestionAnswer, closeOutNettingEnforceable, automaticEarlyTerminationRequired, offShoreInsolvency, onShoreInsolvency, multipleOfficesImpactAnswer]);

    const eachCollateralAnswer: JurisdictionalAnswers[] = useMemo(() => [
        createQuestionAnswer('Will a transfer be respected as a title transfer?', transferRespectedAsTitleTransfer),
        createQuestionAnswer('Does recharacterisation of collateral as security adversely impact enforceability?', recharacterisationAsSecurityAdverselyImpactsEnforceability),
        createQuestionAnswer('Is the right to set off enforceable?', rightToSetOffEnforceable)
    ], [createQuestionAnswer, recharacterisationAsSecurityAdverselyImpactsEnforceability, rightToSetOffEnforceable, transferRespectedAsTitleTransfer]);

    const closeOutNettingDetails = useMemo(() => (
        <div className={styles.questionAndAnswerWrapper}>
            {eachAnswer.map(({ question, answer }, index) => (
                <div className={styles.questionAndAnswer} key={index}>
                    <div className={styles.question}>{question}</div>
                    <div className={styles.answer}>{answer}</div>
                </div>
            ))}
        </div>
    ), [eachAnswer]);

    const collateralNettingDetails = useMemo(() => (
        <div className={styles.questionAndAnswerWrapper}>
            {eachCollateralAnswer.map(({ question, answer }, index) => (
                <div className={styles.questionAndAnswer} key={index}>
                    <div className={styles.question}>{question}</div>
                    <div className={styles.answer}>{answer}</div>
                </div>
            ))}
        </div>
    ), [eachCollateralAnswer]);

    return (
        <>
            <NettingEngineJurisdictionResultsSection label='Close Out Details' children={closeOutNettingDetails} />
            <NettingEngineJurisdictionResultsSection label='Collateral Details' children={collateralNettingDetails} />
        </>
    );
};
